import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, UserCircle2, Sparkles } from "lucide-react";
import { useI18n } from "../lib/i18n.jsx";
import { ACCOUNT_URL } from "../lib/content.js";
import LangPicker from "./LangPicker.jsx";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import logo from "../assets/logo.svg";

const NAV_LINKS = (t) => [
  { href: "/#camps", label: t.nav.camps },
  { href: "/#why", label: t.nav.why },
  { href: "/#programme", label: t.nav.programme },
  { href: "/#skatepark", label: t.nav.skatepark },
  { href: "/#dla-rodzicow", label: t.nav.parents },
  // { href: "/#info", label: t.nav.info },
  { href: "/#pricing", label: t.nav.pricing },
  { href: "/#faq", label: t.nav.faq }
];

export default function Nav() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = NAV_LINKS(t);

  const goToTerms = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/lato") {
      document.getElementById("summer-terms")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (location.pathname === "/zima") {
      document.getElementById("winter-terms")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (location.pathname === "/") {
      document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#pricing");
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-winter/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1300px] mx-auto flex items-center justify-between px-5 md:px-8 h-16 md:h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="Scootive Camp"
          onClick={(e) => {
            if (location.pathname === "/") {
              e.preventDefault();
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center"
        >
          <img src={logo} alt="Scootive Camp" className="h-9 md:h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className="text-[14px] font-semibold text-winter/80 hover:text-summer transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* <LangPicker variant="desktop" /> */}

          <a
            href={ACCOUNT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-account"
            aria-label={t.nav.account}
            title={t.nav.account}
            className="inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full text-winter/80 hover:text-winter transition-colors"
          >
            <UserCircle2 className="w-5 h-5 md:w-[22px] md:h-[22px]" />
          </a>

          <a
            href="#pricing"
            onClick={goToTerms}
            data-testid="nav-cta-signup"
            className="!hidden lg:!inline-flex btn-pill btn-pill-primary text-xs md:text-sm py-2 px-3.5 md:py-2.5 md:px-5"
          >
            {t.nav.signup} →
          </a>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-toggle"
                className="lg:hidden text-winter p-2 -mr-2"
                aria-label="Menu"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-cream text-winter border-l border-winter/10 w-[88vw] sm:w-[420px] p-0"
            >
              <div className="p-6 flex flex-col h-full">
                <nav className="flex flex-col gap-3 flex-1">
                  {links.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={() => setOpen(false)}
                      className="font-display text-3xl font-bold text-winter hover:text-summer transition-colors"
                      data-testid={`nav-mobile-${l.href.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    >
                      {l.label}
                    </a>
                  ))}
                </nav>
                <div className="flex items-center gap-3 mt-8">
                  {/* <LangPicker variant="mobile" /> */}
                  <a
                    href={ACCOUNT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-12 h-12 text-winter"
                    aria-label={t.nav.account}
                    data-testid="nav-mobile-account"
                  >
                    <UserCircle2 className="w-5 h-5" />
                  </a>
                </div>
                <a
                  href="#pricing"
                  onClick={goToTerms}
                  data-testid="nav-mobile-cta"
                  className="mt-3 btn-pill btn-pill-primary py-4"
                >
                  {t.nav.signup} →
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
